import { faCheck, faClipboard, faLink } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { message } from "antd";
import React, { useMemo, useState } from "react";
import GlobalStoreContext from "../../../context/GlobalStoreContext";
import Helper from "../../../Helper";
import { Area } from "../../../models/Area";
import { CalendarEvent } from "../../../models/CalendarEvent";
import { Delivery } from "../../../models/Delivery";
import { Dropoff } from "../../../models/Dropoff";
import { Equipment } from "../../../models/Equipment";
import { Gate } from "../../../models/Gate";
import { GateToDropoffRoute } from "../../../models/GateToDropoffRoute";
import { HaulRoute } from "../../../models/HaulRoute";
import { mappableName } from "../../Helper/Map";
import { ShareItemType } from "../../PlotAPI/Share";
import ShareButton from "./ShareButton";

interface Props {
    shareType: ShareItemType;
    projectToken: string;
    // Optional props
    currentObject?: Area | Dropoff | Equipment | Gate | HaulRoute | GateToDropoffRoute;
    calendarEvent?: CalendarEvent;
    delivery?: Delivery;
}

const ShareProjectItemManual: React.FC<Props> = (props) => {
    const [urlCopied, setUrlCopied] = useState(false);
    const globalStoreContext = React.useContext(GlobalStoreContext);

    if (["location", "dropoff", "equipment"].includes(props.shareType) && !props.currentObject) {
        return <></>;
    }

    const baseURL = useMemo(() => {
        return Helper.baseUrlAltSubdomain(
            globalStoreContext.currentProjectInfo.claimed_by_team_subdomain || "projects"
        );
    }, [globalStoreContext.currentProjectInfo.claimed_by_team_subdomain]);

    const shareURL = () => {
        if (props.delivery && props.calendarEvent) {
            return `${baseURL}/event/${props.delivery.uniqueToken}`;
        } else {
            let url = `${baseURL}/projects/${globalStoreContext.currentProjectInfo.unique_token}`;
            if (props.currentObject) {
                const mapObject = props.currentObject.getMapObject(globalStoreContext);

                if (mapObject) {
                    url += `/portal?mapObject=${mapObject.uuid}`;
                }
            }
            return url;
        }
    };

    const itemLabel = () => {
        switch (props.shareType) {
            case "location":
                return (props.currentObject && mappableName(props.currentObject)) || "Location";
            case "dropoff":
                return (props.currentObject && mappableName(props.currentObject)) || "Drop-off";
            case "equipment":
                return (props.currentObject && mappableName(props.currentObject)) || "Equipment";
            case "project":
                return globalStoreContext.currentProjectInfo.project_name;
            case "delivery":
                return props.calendarEvent?.name || "Delivery";
        }
    };

    const handleCopyUrl = async (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        try {
            await navigator.clipboard.writeText(shareURL());
            setUrlCopied(true);
            setTimeout(() => setUrlCopied(false), 2000);
        } catch (err) {
            message.error("Failed to copy URL");
        }
    };

    const itemTitle = () => {
        if (globalStoreContext.userInfo) {
            return `${globalStoreContext.userInfo.fullname} would like to share ${itemLabel()} with you.`;
        }
        let title = `PLOT Jobsite ${globalStoreContext.currentProjectInfo.project_name} `;
        if (props.shareType === "delivery" && props.delivery && props.calendarEvent) {
            title += `delivery, ${props.calendarEvent.name}`;
        }
        if (props.shareType === "location" && props.currentObject) {
            title += `location, ${mappableName(props.currentObject)}`;
        }
        if (props.shareType === "dropoff" && props.currentObject) {
            title += `drop-off, ${mappableName(props.currentObject)}`;
        }
        if (props.shareType === "project") {
            title += `shared with you`;
        }
        if (props.shareType === "equipment" && props.currentObject) {
            title += `equipment, ${mappableName(props.currentObject)}`;
        }

        return title;
    };

    return (
        <div className="flex items-center gap-2 w-full">
            <button
                onClick={handleCopyUrl}
                className="flex-1 flex items-center gap-2 px-4 py-3 bg-slate-50 border border-slate-200 rounded-lg min-w-0 cursor-pointer"
            >
                {urlCopied ? (
                    <FontAwesomeIcon icon={faCheck} className="text-green-600 shrink-0" />
                ) : (
                    <FontAwesomeIcon icon={faLink} className="text-slate-400 shrink-0" />
                )}

                <p className="truncate text-sm text-slate-600 dark:text-plot-darkmode-white">{shareURL()}</p>
            </button>
            <button
                onClick={handleCopyUrl}
                className={Helper.classNames(
                    "flex items-center gap-2 px-4 py-3 rounded-lg font-medium text-sm transition-all shrink-0",
                    urlCopied
                        ? "bg-green-50 text-green-600 cursor-not-allowed"
                        : "bg-slate-100 hover:bg-slate-200 text-slate-700 cursor-pointer"
                )}
            >
                {urlCopied ? (
                    <>
                        <FontAwesomeIcon icon={faCheck} />
                        <span className={Helper.classNames(globalStoreContext.isMobile ? "flex" : "hidden lg:flex")}>
                            Copied
                        </span>
                    </>
                ) : (
                    <>
                        <FontAwesomeIcon icon={faClipboard} />
                        <span className={Helper.classNames(globalStoreContext.isMobile ? "flex" : "hidden lg:flex")}>
                            Copy Link
                        </span>
                    </>
                )}
            </button>

            <ShareButton url={shareURL()} title={itemTitle()} />
        </div>
    );
};

export default ShareProjectItemManual;
